import { useMemo } from "react";
import { useWalletStore } from "@/stores/wallet-store";
import { useTransferStore } from "@/stores/transfer-store";
import { PROVIDERS, CHAINS } from "@/features/oversight/oversight-data";
import {
  getTotalAum,
  getPendingApprovals,
  getActiveAlerts,
  getOpenExceptions,
  getAttentionItems,
  getAumByWalletType,
  getChainDistributionGrouped,
  getDegradedProviders,
  getDegradedChains,
  getSystemStatusItems,
  getTopChainByAum,
} from "./dashboard-selectors";
import { HeroSection } from "./components/hero";
import { AttentionBar } from "./components/attention-bar";
import { AumComposition } from "./components/aum-composition";
import { ChainDistribution } from "./components/chain-distribution";
import { ProviderChainHealth } from "./components/provider-chain-health";
import { SystemStatus } from "./components/system-status";
import { RecentTransfers } from "./components/recent-transfers";
import { PendingApprovals } from "./components/pending-approvals";
import { OperationalInsight } from "./components/operational-insight";

export default function Dashboard() {
  const wallets = useWalletStore((s) => s.wallets);
  const transfers = useTransferStore((s) => s.transfers);
  const approvals = useTransferStore((s) => s.approvals);

  const totalAum = useMemo(() => getTotalAum(wallets), [wallets]);
  const aumByType = useMemo(() => getAumByWalletType(wallets), [wallets]);
  const chainDistribution = useMemo(() => getChainDistributionGrouped(wallets, 0.05), [wallets]);
  const pendingApprovals = useMemo(() => getPendingApprovals(approvals), [approvals]);
  const activeAlerts = useMemo(() => getActiveAlerts(), []);
  const openExceptions = useMemo(() => getOpenExceptions(), []);
  const degradedProviders = useMemo(() => getDegradedProviders(), []);
  const degradedChains = useMemo(() => getDegradedChains(), []);
  const attentionItems = useMemo(
    () => getAttentionItems(pendingApprovals, activeAlerts, openExceptions),
    [pendingApprovals, activeAlerts, openExceptions]
  );
  const systemStatusItems = useMemo(
    () => getSystemStatusItems(activeAlerts, openExceptions, degradedProviders, degradedChains),
    [activeAlerts, openExceptions, degradedProviders, degradedChains]
  );
  const topChain = useMemo(() => getTopChainByAum(wallets), [wallets]);
  const trackedChainHealth = useMemo(
    () => CHAINS.filter((c) => chainDistribution.some((i) => i.chain === c.name)),
    [chainDistribution]
  );
  const recentTransfers = useMemo(() => transfers, [transfers]);

  return (
    <div className="space-y-4 pb-8">
      <HeroSection />
      <AttentionBar items={attentionItems} />

      {/* Primary analytical area */}
      <div className="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-[1fr_360px]">
        <section className="flex h-full flex-col rounded-2xl border border-line bg-surface p-5 md:p-6">
          <AumComposition items={aumByType} total={totalAum} wallets={wallets} />
        </section>
        <section className="flex h-full flex-col rounded-2xl border border-line bg-surface p-5 md:p-6">
          <ChainDistribution items={chainDistribution} total={totalAum} wallets={wallets} chainHealth={trackedChainHealth} />
        </section>
      </div>

      {/* Infrastructure health */}
      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[1fr_360px]">
        <section className="rounded-2xl border border-line bg-surface p-5 md:p-6">
          <ProviderChainHealth providers={PROVIDERS} chains={CHAINS} />
        </section>
        <section className="rounded-2xl border border-line bg-surface p-5 md:p-6">
          <SystemStatus items={systemStatusItems} />
        </section>
      </div>

      {/* Activity + approvals */}
      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[1fr_360px]">
        <section className="rounded-2xl border border-line bg-surface p-5 md:p-6">
          <RecentTransfers transfers={recentTransfers} />
        </section>
        <section className="rounded-2xl border border-line bg-surface p-5 md:p-6">
          <PendingApprovals approvals={pendingApprovals} />
        </section>
      </div>

      <OperationalInsight
        attentionItems={attentionItems}
        pendingCount={pendingApprovals.length}
        alertCount={activeAlerts.length}
        providerDegradedCount={degradedProviders.length}
        topChain={topChain}
      />
    </div>
  );
}
